import { UploadedMediaItem } from "@/types/media";
import type { UploadFile } from "antd/es/upload/interface";

type MediaType = "images" | "videos";
type PickerMode = "files" | "directory";

export const getAccessToken = () => {
    const local = localStorage.getItem("AUTHDATA");
    const session = sessionStorage.getItem("AUTHDATA");
    const auth = local || session;
    if (auth) {
        try {
            const parsed = JSON.parse(auth);
            return parsed.token || process.env.DEFAULT_TOKEN;
        } catch {
            return process.env.DEFAULT_TOKEN;
        }
    }
    return process.env.DEFAULT_TOKEN;
};

export const localData = {
    get: <T>(key: string): T | null => {
        if (!key) return null;
        try {
            const data = localStorage.getItem(key);
            if (!data) return null;
            return JSON.parse(data) as T;
        } catch (error) {
            console.log("Error parsing localStorage data:", error);
        }
        return null;
    },
    set: (key: string, data: any): void => {
        if (!key) return;
        try {
            localStorage.setItem(key, JSON.stringify(data));
        } catch (error) {
            console.log("Error saving localStorage data:", error);
        }
    },
    remove: (key: string): void => {
        if (!key) return;
        try {
            const data = localStorage.getItem(key);
            if (!data) return;
            localStorage.removeItem(key);
        } catch (error) {
            console.error("localData.remove error", error);
        }
        return;
    },
    has: (key: string): boolean => {
        if (!key) return false;
        const data = localStorage.getItem(key);
        if (data) return true;
        return false;
    },
};

export const sessionData = {
    get: <T>(key: string): T | null => {
        if (!key) return null;
        try {
            const data = sessionStorage.getItem(key);
            if (!data) return null;
            return JSON.parse(data) as T;
        } catch (error) {
            console.log("Error parsing localStorage data:", error);
        }
        return null;
    },
    set: (key: string, data: any): void => {
        if (!key) return;
        try {
            sessionStorage.setItem(key, JSON.stringify(data));
        } catch (error) {
            console.log("Error saving localStorage data:", error);
        }
    },
    has: (key: string): boolean => {
        if (!key) return false;
        const data = sessionStorage.getItem(key);
        if (data) return true;
        return false;
    },
    remove: (key: string): void => {
        if (!key) return;
        sessionStorage.removeItem(key);
        return;
    },
};

export const createGuid = (): string => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

export const compareObject = (obj1: any, obj2: any): boolean => {
    if (obj1 === obj2) return true;
    if (obj1 == null || obj2 == null) return false;

    if (obj1 instanceof Date && obj2 instanceof Date) {
        return obj1.getTime() === obj2.getTime();
    }

    if (typeof obj1 !== "object" || typeof obj2 !== "object") return false;

    const isArrayA = Array.isArray(obj1);
    const isArrayB = Array.isArray(obj2);
    if (isArrayA !== isArrayB) return false;

    if (isArrayA && isArrayB) {
        if (obj1.length !== obj2.length) return false;
        for (let i = 0; i < obj1.length; i++) {
            if (!compareObject(obj1[i], obj2[i])) return false;
        }
        return true;
    }

    const keysA = Object.keys(obj1);
    const keysB = Object.keys(obj2);
    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
        if (!Object.prototype.hasOwnProperty.call(obj2, key)) return false;
        if (!compareObject(obj1[key], obj2[key])) return false;
    }
    return true;
};

export const RAW_IMAGE_EXTENSIONS = [
    ".3fr",
    ".arw",
    ".cr2",
    ".cr3",
    ".dcr",
    ".dng",
    ".erf",
    ".fff",
    ".iiq",
    ".k25",
    ".kdc",
    ".mef",
    ".mos",
    ".mrw",
    ".nef",
    ".nrw",
    ".orf",
    ".pef",
    ".raf",
    ".raw",
    ".rw2",
    ".rwl",
    ".sr2",
    ".srf",
    ".srw",
    ".x3f",
];

export const RAW_IMAGE_MIME_TYPES = new Set([
    "image/x-adobe-dng",
    "image/x-canon-cr2",
    "image/x-canon-cr3",
    "image/x-fuji-raf",
    "image/x-kodak-dcr",
    "image/x-minolta-mrw",
    "image/x-nikon-nef",
    "image/x-nikon-nrw",
    "image/x-olympus-orf",
    "image/x-panasonic-rw2",
    "image/x-pentax-pef",
    "image/x-sony-arw",
    "image/x-samsung-srw",
    "application/dng",
    "application/x-adobe-dng",
    "application/octet-stream",
]);

export const UPLOADED_IMAGE_PATTERN =
    /\.(3fr|arw|bmp|cr2|cr3|dcr|dng|erf|fff|gif|iiq|jpe?g|k25|kdc|mef|mos|mrw|nef|nrw|orf|pef|png|raf|raw|rw2|rwl|sr2|srf|srw|webp|x3f)$/;
export const UPLOADED_VIDEO_PATTERN = /\.(mp4|mov|avi|mkv|webm)$/;

export const RAW_IMAGE_ACCEPT_VALUE = RAW_IMAGE_EXTENSIONS.join(",");
export const IMAGE_ACCEPT_VALUE = `image/*,${RAW_IMAGE_ACCEPT_VALUE}`;
export const MEDIA_ACCEPT_VALUE = `${IMAGE_ACCEPT_VALUE},video/*`;

export const sanitizeFileName = (fileName: string) => fileName.replace(/\s+/g, "-");

export const getLowerCaseFileName = (file: Pick<File, "name"> | Pick<UploadFile, "name">) =>
    (file.name || "").toLowerCase();

export const hasRawImageExtension = (file: Pick<File, "name"> | Pick<UploadFile, "name">) =>
    RAW_IMAGE_EXTENSIONS.some((extension) => getLowerCaseFileName(file).endsWith(extension));

export const isRawImageName = (fileName?: string) => {
    if (!fileName) return false;
    return RAW_IMAGE_EXTENSIONS.some((extension) => fileName.toLowerCase().endsWith(extension));
};

export const isImageFile = (
    file: Pick<File, "name" | "type"> | Pick<UploadFile, "name" | "type">,
) => {
    const normalizedType = (file.type || "").toLowerCase();

    if (normalizedType.startsWith("image/")) return true;
    if (RAW_IMAGE_MIME_TYPES.has(normalizedType) && hasRawImageExtension(file)) return true;
    if (!normalizedType) return hasRawImageExtension(file);

    return hasRawImageExtension(file);
};

export const isVideoFile = (file: Pick<File, "type"> | Pick<UploadFile, "type">) =>
    Boolean(file.type?.toLowerCase().startsWith("video/"));

export const getMediaCategory = (
    file: Pick<File, "name" | "type"> | Pick<UploadFile, "name" | "type">,
) => {
    if (isImageFile(file)) return "images";
    if (isVideoFile(file)) return "videos";
    return null;
};

export const decodeMediaFileName = (fileName?: string) => {
    if (!fileName) return "";

    try {
        return decodeURIComponent(fileName);
    } catch {
        return fileName;
    }
};

export const normalizeMediaFileName = (fileName?: string) =>
    decodeMediaFileName(fileName).trim().toLowerCase();

export const normalizeUploadedMediaItem = (file: UploadedMediaItem): UploadedMediaItem => ({
    ...file,
    name: decodeMediaFileName(file.name),
});

export const mergeUploadedMediaItems = (items: UploadedMediaItem[]) => {
    const mergedItems = new Map<string, UploadedMediaItem>();

    items.forEach((item) => {
        const normalizedItem = normalizeUploadedMediaItem(item);
        const key = normalizeMediaFileName(normalizedItem.name);

        if (!key) return;

        const existingItem = mergedItems.get(key);
        if (!existingItem) {
            mergedItems.set(key, normalizedItem);
            return;
        }

        mergedItems.set(key, {
            ...existingItem,
            ...normalizedItem,
            url: existingItem.url || normalizedItem.url,
            thumbUrl: existingItem.thumbUrl || normalizedItem.thumbUrl,
            size: existingItem.size || normalizedItem.size,
        });
    });

    return Array.from(mergedItems.values());
};

export const getUploadedMediaType = (fileName?: string): MediaType | null => {
    const normalizedFileName = normalizeMediaFileName(fileName);

    if (UPLOADED_IMAGE_PATTERN.test(normalizedFileName)) return "images";
    if (UPLOADED_VIDEO_PATTERN.test(normalizedFileName)) return "videos";

    return null;
};

export const filterUploadedMediaItemsByType = (items: UploadedMediaItem[], type: MediaType) =>
    items.filter((item) => getUploadedMediaType(item.name) === type);

export const createUploadListItem = async (file: File): Promise<UploadFile> => {
    const thumbUrl =
        isImageFile(file) && !isRawImageName(file.name) ? URL.createObjectURL(file) : "";

    return {
        uid: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        name: sanitizeFileName(file.name),
        size: file.size,
        type: file.type,
        percent: 0,
        status: "done",
        originFileObj: file as UploadFile["originFileObj"],
        thumbUrl: thumbUrl,
        preview: thumbUrl,
    };
};

export const decodedUploadedImageUrls = new Set<string>();
export const pendingUploadedImageDecodes = new Map<string, Promise<void>>();

export const decodeUploadedImage = (src: string) => {
    const pendingDecode = pendingUploadedImageDecodes.get(src);
    if (pendingDecode) return pendingDecode;

    const decodePromise = new Promise<void>((resolve, reject) => {
        const img = new Image();
        let settled = false;

        const resolveOnce = () => {
            if (settled) return;
            settled = true;
            decodedUploadedImageUrls.add(src);
            pendingUploadedImageDecodes.delete(src);
            resolve();
        };

        const rejectOnce = () => {
            if (settled) return;
            settled = true;
            pendingUploadedImageDecodes.delete(src);
            reject(new Error(`Failed to decode image: ${src}`));
        };

        img.decoding = "async";
        img.onload = resolveOnce;
        img.onerror = rejectOnce;
        img.src = src;

        if (img.complete && img.naturalWidth > 0) {
            resolveOnce();
            return;
        }

        if (typeof img.decode === "function") {
            img.decode()
                .then(resolveOnce)
                .catch(() => {
                    if (img.complete && img.naturalWidth > 0) resolveOnce();
                });
        }
    });

    pendingUploadedImageDecodes.set(src, decodePromise);
    return decodePromise;
};

export function getShortCode(str: string) {
    if (!str) return "";
    const words: string[] = str.match(/[A-Z]?[a-z]+|[A-Z]+(?![a-z])/g) ?? [];

    if (!words.length) {
        return "";
    }

    if (words.length === 1) {
        return words[0].slice(0, 3).toUpperCase();
    }

    return (
        words[0].slice(0, 2) +
        words
            .slice(1)
            .map((word: string) => word[0])
            .join("")
    ).toUpperCase();
}

export const getAccessColor = (access: string): string => {
    const colors: { [key: string]: string } = {
        view: "blue",
        add: "green",
        edit: "orange",
        delete: "red",
        upgrade: "purple",
    };
    return colors[access] || "default";
};

export const getRoleColor = (role: string): string => {
    const colors: { [key: string]: string } = {
        superadmin: "red",
        studioadmin: "purple",
        studiomanager: "cyan",
        studiophotographer: "blue",
        user: "green",
    };
    return colors[role] || "default";
};

export const getClientPassword = () => {
    return process.env.AUTHBRIDGE_PASSWORD;
};

export const deriveKeyBytes = async (password: string) => {
    const enc = new TextEncoder();
    const pwBuf = enc.encode((password || "").trim());
    const hashBuf = await window.crypto.subtle.digest("SHA-512", pwBuf);
    const hashArray = Array.from(new Uint8Array(hashBuf));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    const keyHexSub = hashHex.substring(0, 16); // server uses first 16 hex chars
    return enc.encode(keyHexSub); // UTF-8 bytes of the hex substring
};

export const decryptAes128Cbc = async (encrypted: string, password: string) => {
    if (!encrypted) throw new Error("No encrypted payload");
    const parts = encrypted.split(":");
    if (parts.length !== 2) throw new Error("Invalid encrypted format");
    const [cipherB64, ivB64] = parts;
    const keyBytes = await deriveKeyBytes(password);
    const key = await window.crypto.subtle.importKey("raw", keyBytes, { name: "AES-CBC" }, false, [
        "decrypt",
    ]);
    const cipherBytes = Uint8Array.from(atob(cipherB64), (c) => c.charCodeAt(0));
    const ivBytes = Uint8Array.from(atob(ivB64), (c) => c.charCodeAt(0));
    const plainBuf = await window.crypto.subtle.decrypt(
        { name: "AES-CBC", iv: ivBytes },
        key,
        cipherBytes,
    );
    return new TextDecoder().decode(new Uint8Array(plainBuf));
};
